import { useCallback, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import type {
  AcceptLegalAgreementsRequestT,
  AddEditClinicianRequestT,
  ClinicianIdentityProofingRequestT,
  SubmitCredentialingRequestT,
  SubmitNativeApplicationRequestT,
  UpdateBankingDetailsRequestT,
  UpdateEmployeeRecordRequestT,
} from '@lotushealth/lotushealth_js_sdk/schemas';

import {
  useSubmitNativeApplicationMutation,
  useSubmitCredentialingMutation,
} from '@/entities/clinician-application';
import { useLinkBankAccountMutation } from '@/entities/clinician-bank-link';
import {
  useCreateDosespotClinicianMutation,
  useRefreshMyDosespotClinicianInfoMutation,
  useUpdateDosespotClinicianMutation,
} from '@/entities/dosespot-clinician';
import {
  useAcceptIdpDisclaimerMutation,
  useAcceptLegalAgreementsMutation,
  useStartIdpProcessMutation,
  useSubmitDemographicInfoMutation,
  useSubmitIdpAnswersMutation,
  useSubmitIdpOtpMutation,
} from '@/entities/dosespot-idp';
import { useConsumeInviteTokenMutation, useUpdateMyEmployeeRecordMutation } from '@/entities/me';
import { logger } from '@/shared';
import { clearPendingInviteToken, getPendingInviteToken } from '@/shared/lib/invite-token';

import { clinicianOnboardingKeys, useCurrentStepQuery } from '../api';
import { toDobIso } from './dosespot';
import type { OnboardingStep } from './onboarding-step';

export interface EmployeeRecordPayload {
  displayName: string;
  email: string;
  licensedStates: string[] | null;
  licensedPracticeAreas: string[] | null;
}

export interface PrescriberDetailsPayload {
  clinicianInfo: AddEditClinicianRequestT;
  userId: string;
}

export interface LegalAgreementsPayload {
  nameSigned: AcceptLegalAgreementsRequestT['nameSigned'];
}

export interface DemographicInfoPayload {
  demographicInfo: ClinicianIdentityProofingRequestT;
}

export interface AnswersPayload {
  answers: string[];
}

export interface OtpPayload {
  otp: string;
}

export interface LinkBankAccountPayload {
  accountHolderName: UpdateBankingDetailsRequestT['accountHolderName'];
  routingNumber: UpdateBankingDetailsRequestT['routingNumber'];
  accountNumber: UpdateBankingDetailsRequestT['accountNumber'];
  accountType: UpdateBankingDetailsRequestT['accountType'];
}

export interface InitialScreeningPayload {
  data: SubmitNativeApplicationRequestT;
}

export interface CredentialingPayload {
  data: SubmitCredentialingRequestT;
}

export interface ClinicianOnboardingOrchestrator {
  step: OnboardingStep;
  submit: (payload?: unknown) => void;
  refresh: () => Promise<void>;
  isSubmitting: boolean;
  submitError: Error | null;
  resetError: () => void;
}

export function useClinicianOnboardingOrchestrator(): ClinicianOnboardingOrchestrator {
  const queryClient = useQueryClient();

  const [inviteToken, setInviteToken] = useState<string | null>(() => getPendingInviteToken());

  const currentStepQuery = useCurrentStepQuery(!inviteToken);

  let step: OnboardingStep;
  if (inviteToken) {
    step = { kind: 'consuming_invite', token: inviteToken };
  } else if (currentStepQuery.isLoading) {
    step = { kind: 'loading' };
  } else if (currentStepQuery.isError) {
    step = {
      kind: 'fetch_error',
      message: 'Could not load your onboarding status.',
    };
  } else if (currentStepQuery.data === undefined) {
    step = {
      kind: 'fetch_error',
      message: 'Could not determine your onboarding step.',
    };
  } else {
    step = currentStepQuery.data;
  }

  const consumeInvite = useConsumeInviteTokenMutation();
  const submitNativeApplication = useSubmitNativeApplicationMutation();
  const submitCredentialing = useSubmitCredentialingMutation();
  const updateEmployee = useUpdateMyEmployeeRecordMutation();
  const createClinician = useCreateDosespotClinicianMutation();
  const updateClinician = useUpdateDosespotClinicianMutation();
  const refreshClinician = useRefreshMyDosespotClinicianInfoMutation();
  const startIdp = useStartIdpProcessMutation();
  const acceptDisclaimer = useAcceptIdpDisclaimerMutation();
  const acceptLegal = useAcceptLegalAgreementsMutation();
  const submitDemographics = useSubmitDemographicInfoMutation();
  const submitAnswers = useSubmitIdpAnswersMutation();
  const submitOtp = useSubmitIdpOtpMutation();
  const linkBankAccount = useLinkBankAccountMutation();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<Error | null>(null);
  const resetError = useCallback(() => setSubmitError(null), []);

  const advance = useCallback(async () => {
    await queryClient.invalidateQueries({
      queryKey: clinicianOnboardingKeys.currentStep(),
    });
  }, [queryClient]);

  async function submit(payload?: unknown): Promise<void> {
    setSubmitError(null);
    setIsSubmitting(true);

    try {
      await dispatch(step, payload);
      await advance();
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Submission failed');
      setSubmitError(error);
      logger.error(
        'clinician onboarding submit failed',
        {
          surface: 'handled',
          feature: 'clinician-onboarding',
          operation: 'submit',
          step: step.kind,
          errorMessage: error.message,
        },
        error
      );
    } finally {
      setIsSubmitting(false);
    }
  }

  return {
    step,
    submit,
    refresh: advance,
    isSubmitting,
    submitError,
    resetError,
  };

  async function dispatch(currentStep: OnboardingStep, payload: unknown): Promise<void> {
    switch (currentStep.kind) {
      case 'consuming_invite': {
        try {
          await consumeInvite.mutateAsync({ token: currentStep.token });
          clearPendingInviteToken();
          setInviteToken(null);
        } catch (error) {
          const status = isAxiosError(error) ? error.response?.status : undefined;
          const isTerminalInviteError = status !== undefined && status >= 400 && status < 500;

          if (isTerminalInviteError) {
            clearPendingInviteToken();
            setInviteToken(null);
          }

          throw error;
        }
        break;
      }

      case 'fill_employee_record': {
        const { displayName, email, licensedStates, licensedPracticeAreas } =
          payload as EmployeeRecordPayload;
        await updateEmployee.mutateAsync({
          data: {
            displayName,
            email,
            licensedStates,
            licensedPracticeAreas,
          } satisfies UpdateEmployeeRecordRequestT,
        });
        break;
      }

      case 'enter_prescriber_details': {
        const { clinicianInfo, userId } = payload as PrescriberDetailsPayload;
        const isCreate = currentStep.data.prefill === null;

        if (isCreate) {
          await createClinician.mutateAsync({ data: { userId, clinicianInfo } });
        } else {
          await updateClinician.mutateAsync({ userId, data: { clinicianInfo } });
        }

        try {
          await refreshClinician.mutateAsync();
        } catch (refreshErr) {
          logger.error(
            'clinician onboarding refresh after prescriber save failed',
            {
              surface: 'handled',
              feature: 'clinician-onboarding',
              operation: 'refresh-after-save',
              step: currentStep.kind,
              errorMessage: refreshErr instanceof Error ? refreshErr.message : 'Unknown',
            },
            refreshErr
          );
        }
        break;
      }

      case 'start_idp':
        await startIdp.mutateAsync();
        break;

      case 'sign_disclaimer':
        await acceptDisclaimer.mutateAsync();
        break;

      case 'sign_legal_agreements': {
        const { nameSigned } = payload as LegalAgreementsPayload;
        await acceptLegal.mutateAsync({ data: { nameSigned } });
        break;
      }

      case 'submit_demographics': {
        const { demographicInfo } = payload as DemographicInfoPayload;
        await submitDemographics.mutateAsync({ data: { demographicInfo } });
        break;
      }

      case 'answer_questions': {
        const { answers } = payload as AnswersPayload;
        await submitAnswers.mutateAsync({ data: { answers } });
        break;
      }

      case 'submit_otp': {
        const { otp } = payload as OtpPayload;
        await submitOtp.mutateAsync({ data: { otp } });
        break;
      }

      case 'link_bank_account': {
        const { accountHolderName, routingNumber, accountNumber, accountType } =
          payload as LinkBankAccountPayload;
        await linkBankAccount.mutateAsync({
          data: {
            accountHolderName,
            routingNumber,
            accountNumber,
            accountType,
          },
        });
        break;
      }

      case 'submit_application': {
        const { data } = payload as InitialScreeningPayload;
        const result = await submitNativeApplication.mutateAsync({ data });
        if (result.type === 'failure') {
          throw new Error(result.details);
        }
        break;
      }

      case 'fill_credentialing_form': {
        const { data } = payload as CredentialingPayload;
        const result = await submitCredentialing.mutateAsync({
          data: { ...data, date_of_birth: toDobIso(data.date_of_birth) },
        });
        if (result.type === 'failure') {
          throw new Error(result.details);
        }
        break;
      }

      case 'application_pending':
      case 'application_rejected':
      case 'credentialing_pending':
      case 'awaiting_dosespot':
      case 'loading':
      case 'fetch_error':
      case 'invite_failed':
      case 'complete':
        break;

      default: {
        const _exhaustive: never = currentStep;
        void _exhaustive;
      }
    }
  }
}
